import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import {
  MessageOptionsType,
  SnackbarMessage,
  SnackbarPosition,
} from '@/common/types'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './snackbar.scss?inline'

if (typeof window !== 'undefined') {
  window.showDapSnackbar = (message: string, options?: MessageOptionsType) => {
    DapDSSnackbar.show(message, options)
  }

  window.closeAllDapSnackbars = () => {
    DapDSSnackbar.closeAll()
  }
}

/**
 * `dap-ds-snackbar`
 * @summary The Snackbar is a container of sliding messages.
 * @element dap-ds-snackbar
 * @title - Snackbar
 *
 * @csspart host - The host element
 *
 * @cssproperty --dds-snackbar-spacing - Spacing used for positioning the snackbar (default: var(--dds-spacing-400))
 * @cssproperty --dds-snackbar-z-index - Z-index of the snackbar container (default: 10000)
 * @cssproperty --dds-snackbar-pointer-events - Pointer events behavior for the snackbar (default: auto)
 * @cssproperty --dds-snackbar-animation-duration - Duration of entrance animations (default: 0.3s)
 * @cssproperty --dds-snackbar-animation-timing - Timing function for animations (default: ease-out)
 */
@customElement('dap-ds-snackbar')
export default class DapDSSnackbar extends DdsElement {
  /** The maximum number of snackbar messages at a given time. */
  @property({ type: Number }) maxItems = 4

  /** The position of the snackbar
   * @type {'bottom-left' | 'bottom-right' | 'bottom-center' | 'top-right' | 'top-center'}
   */
  @property({ type: String }) position: SnackbarPosition = 'bottom-right'

  /** The duration of the snackbar message in milliseconds */
  @property({ type: Number }) duration = 6000

  /** Whether the snackbar should announce new messages to screen readers */
  @property({ type: Boolean }) announceMessages = true

  @state() private messages: SnackbarMessage[] = []
  @state() private idCounter = 0
  private timeouts: Map<string, ReturnType<typeof setTimeout>> = new Map()

  static readonly styles = unsafeCSS(styles)

  /**
   * Static method to show a snackbar message anywhere in the application
   * @param message - The message to display
   * @param options - Optional configuration for the message
   */
  static show(message: string, options?: MessageOptionsType): void {
    document.dispatchEvent(
      new CustomEvent('dds-show-snackbar', {
        detail: { message, options },
        bubbles: true,
      }),
    )
  }

  /**
   * Static method to show a success message
   * @param message - The message to display
   * @param options - Optional configuration (alertType will be overridden to 'successful')
   */
  static success(
    message: string,
    options?: Omit<MessageOptionsType, 'alertType'>,
  ): void {
    this.show(message, { ...options, alertType: 'successful' })
  }

  /**
   * Static method to show an error message
   * @param message - The message to display
   * @param options - Optional configuration (alertType will be overridden to 'error')
   */
  static error(
    message: string,
    options?: Omit<MessageOptionsType, 'alertType'>,
  ): void {
    this.show(message, { ...options, alertType: 'error' })
  }

  /**
   * Static method to show an information message
   * @param message - The message to display
   * @param options - Optional configuration (alertType will be overridden to 'information')
   */
  static info(
    message: string,
    options?: Omit<MessageOptionsType, 'alertType'>,
  ): void {
    this.show(message, { ...options, alertType: 'information' })
  }

  /**
   * Static method to show a default message
   * @param message - The message to display
   * @param options - Optional configuration (alertType will be overridden to 'default')
   */
  static default(
    message: string,
    options?: Omit<MessageOptionsType, 'alertType'>,
  ): void {
    this.show(message, { ...options, alertType: 'default' })
  }

  /**
   * Static method to close all snackbar messages globally
   */
  static closeAll(): void {
    document.dispatchEvent(
      new CustomEvent('dds-close-all-snackbars', {
        bubbles: true,
      }),
    )
  }

  /**
   * Close all currently displayed messages
   */
  closeAll(): void {
    this.clearAllTimeouts()
    this.messages = []
  }

  private generateId(): string {
    this.idCounter++

    return `dap-snackbar-${this.idCounter}`
  }

  addMessage(message: string, options?: MessageOptionsType): void {
    if (!message || typeof message !== 'string') {
      console.warn('DapDSSnackbar: Message must be a non-empty string')
      return
    }

    const newId = this.generateId()
    if (this.messages.length >= this.maxItems) {
      this.sliceMessage()
    }

    const newMessage: SnackbarMessage = {
      id: newId,
      message: message.trim(),
      ...(options && { options }),
    }

    this.messages = [...this.messages, newMessage]
    this.elapseMessages(newId, options?.duration)

    // Announce the message if enabled
    if (this.announceMessages) {
      this.announceToScreenReader(message)
    }
  }

  private sliceMessage(): void {
    if (this.messages) {
      this.messages = this.messages.slice(1)
    }
  }

  private removeMessage(id: string): void {
    if (id) {
      this.messages = this.messages.filter(
        (messageItem: SnackbarMessage) => messageItem.id !== id,
      )
      this.clearTimeout(id)
    }
  }

  private elapseMessages(id: string, duration?: number): void {
    const durationTime = duration ?? this.duration
    const timeoutId = setTimeout(() => {
      this.removeMessage(id)
    }, durationTime)
    this.timeouts.set(id, timeoutId)
  }

  private clearTimeout(id: string): void {
    const timeoutId = this.timeouts.get(id)
    if (timeoutId) {
      clearTimeout(timeoutId)
      this.timeouts.delete(id)
    }
  }

  private clearAllTimeouts(): void {
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId))
    this.timeouts.clear()
  }

  connectedCallback(): void {
    super.connectedCallback()
    document.addEventListener(
      'dds-show-snackbar',
      this.handleShowSnackbarEvent as EventListener,
    )
    document.addEventListener(
      'dds-close-all-snackbars',
      this.handleCloseAllEvent as EventListener,
    )
    document.addEventListener('keydown', this.handleKeyDown)
  }

  disconnectedCallback(): void {
    this.clearAllTimeouts()
    document.removeEventListener(
      'dds-show-snackbar',
      this.handleShowSnackbarEvent as EventListener,
    )
    document.removeEventListener(
      'dds-close-all-snackbars',
      this.handleCloseAllEvent as EventListener,
    )
    document.removeEventListener('keydown', this.handleKeyDown)
    super.disconnectedCallback()
  }

  private handleShowSnackbarEvent = (
    event: CustomEvent<{ message: string; options?: MessageOptionsType }>,
  ) => {
    const { message, options } = event.detail
    this.addMessage(message, options)
  }

  private handleCloseAllEvent = () => {
    this.closeAll()
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.messages.length > 0) {
      // Close the most recent message
      const lastMessage = this.messages[this.messages.length - 1]
      if (lastMessage) {
        this.removeMessage(lastMessage.id)
      }
    }
  }

  private announceToScreenReader(message: string): void {
    const announcer = document.createElement('div')
    announcer.setAttribute('aria-live', 'assertive')
    announcer.setAttribute('aria-atomic', 'true')
    announcer.style.position = 'absolute'
    announcer.style.left = '-10000px'
    announcer.style.width = '1px'
    announcer.style.height = '1px'
    announcer.style.overflow = 'hidden'

    document.body.appendChild(announcer)
    announcer.textContent = message

    // Remove the announcer after a short delay
    setTimeout(() => {
      document.body.removeChild(announcer)
    }, 1000)
  }

  private _closeMessage(event: CustomEvent<{ id: string }>) {
    if (event?.detail?.id) {
      this.removeMessage(event.detail.id)
    }
  }

  render() {
    return html`<div
      class=${clsx('snackbar', `snackbar--${this.position}`)}
      role="region"
      aria-live="polite"
      aria-label="Notifications">
      ${this.messages.map(
        messageItem => html`
          <dap-ds-snackbar-message
            @dds-close=${this._closeMessage}
            id=${messageItem.id}
            message=${messageItem.message}
            alertType=${ifDefined(messageItem?.options?.alertType)}
            .actions=${messageItem?.options?.actions}
            closeButton=${ifDefined(messageItem?.options?.closeButton)}
            .customStyle=${messageItem?.options?.customStyle}
            position=${this.position}></dap-ds-snackbar-message>
        `,
      )}
    </div>`
  }
}
